import { ColorPalette, DEFAULT_TEXT_COLORS } from '../../models/end-client-reporting-package';
import { SURFACE_LUMINANCE, neutralFrom, readableInk, tint } from './report-aggregation';

/** How far toward white a chosen ink is taken for the deck's secondary copy. */
const MUTED_INK_TINT = 0.28;

/** And for its softer ink -- the key takeaway, the index's subsection names. */
const SOFT_INK_TINT = 0.08;

/**
 * A palette as the `--ecr-*` custom properties every module paints itself from. Shared by the deck
 * and anything else that draws modules -- the module guide -- so a module looks the same in both,
 * custom theme included.
 *
 * Tints and inks are derived rather than picked by hand, so a client's own colours get the same
 * treatment the defaults do -- `tint()` is the function the charts shade with, and `readableInk()`
 * picks which of the deck's two text colours a fill carries. A null value means "leave it to the
 * stylesheet", which is to be removed rather than set.
 */
export function deckThemeTokens(p: ColorPalette): Record<string, string | null> {
  // The deck's two text colours: its ink on the white cards, and what a dark fill carries. A
  // palette from before these existed has neither and takes the ink and white it always had.
  const darkText = p.darkText ?? DEFAULT_TEXT_COLORS.darkText;
  const lightText = p.lightText ?? DEFAULT_TEXT_COLORS.lightText;

  return {
    '--ecr-primary': p.primary,
    '--ecr-secondary': p.secondary,
    '--ecr-accent': p.accent,
    '--ecr-positive': p.positive,
    '--ecr-negative': p.negative,
    '--ecr-ink': darkText,
    '--ecr-light-ink': lightText,
    // Secondary copy -- axis labels, "n =" lines -- follows a chosen ink, a step lighter. Left to
    // the stylesheet's own grey where none is chosen, so a deck that never set one looks the same.
    '--ecr-mut': p.darkText ? tint(darkText, MUTED_INK_TINT) : null,
    '--ecr-ink-soft': p.darkText ? tint(darkText, SOFT_INK_TINT) : null,
    // Ink for text sitting directly on a palette fill: whichever of the two reads better on it, so a
    // light brand colour carries the dark text rather than white it cannot hold.
    '--ecr-on-primary': readableInk(p.primary, darkText, lightText),
    '--ecr-on-secondary': readableInk(p.secondary, darkText, lightText),
    // The surfaces: the palette's own hue at the prototype's three fixed brightnesses, so a deck
    // whose palette is warm does not sit on this one's cold blue-grey. See `neutralFrom`.
    '--ecr-bg': neutralFrom(p.primary, SURFACE_LUMINANCE.background),
    '--ecr-line': neutralFrom(p.primary, SURFACE_LUMINANCE.line),
    '--ecr-grey': neutralFrom(p.primary, SURFACE_LUMINANCE.inert),
    // Washes: the colour present but quiet enough to read black text on -- the selected nav item,
    // filter chips, the key-takeaway callout, a failed-save banner.
    '--ecr-primary-wash': tint(p.primary, 0.9),
    '--ecr-secondary-wash': tint(p.secondary, 0.9),
    '--ecr-positive-wash': tint(p.positive, 0.93),
  };
}

/** Writes the tokens onto an element, removing the ones left to the stylesheet. */
export function applyDeckTheme(element: HTMLElement, palette: ColorPalette): void {
  for (const [name, value] of Object.entries(deckThemeTokens(palette))) {
    if (value === null) element.style.removeProperty(name);
    else element.style.setProperty(name, value);
  }
}
